import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, RotateCcw, ChevronRight, ChevronLeft, 
  BookOpen, Target, Award, HelpCircle 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';

var KEYS_FIFTHS = ['C','G','D','A','E','B','F#','Db','Ab','Eb','Bb','F'];
var MINOR_KEYS = ['Am','Em','Bm','F#m','C#m','G#m','D#m','Bbm','Fm','Cm','Gm','Dm'];
var SHARPS_FLATS = [
  { key: 'C', sharps: 0, flats: 0, sig: 'None' },
  { key: 'G', sharps: 1, flats: 0, sig: '1#' },
  { key: 'D', sharps: 2, flats: 0, sig: '2#' },
  { key: 'A', sharps: 3, flats: 0, sig: '3#' },
  { key: 'E', sharps: 4, flats: 0, sig: '4#' },
  { key: 'B', sharps: 5, flats: 0, sig: '5#' },
  { key: 'F#', sharps: 6, flats: 0, sig: '6#' },
  { key: 'Db', sharps: 0, flats: 5, sig: '5b' },
  { key: 'Ab', sharps: 0, flats: 4, sig: '4b' },
  { key: 'Eb', sharps: 0, flats: 3, sig: '3b' },
  { key: 'Bb', sharps: 0, flats: 2, sig: '2b' },
  { key: 'F', sharps: 0, flats: 1, sig: '1b' }
];

var LEARN_STEPS = [
  { title: 'What is the Circle of 5ths?', text: 'The Circle of 5ths is a visual map of all 12 musical keys arranged by the interval of a perfect 5th. Moving clockwise, each key is a 5th higher. Moving counter-clockwise, each key is a 4th higher (or a 5th lower).', highlight: [] },
  { title: 'Starting from C', text: 'C major has no sharps or flats — it\'s our home base. From here, every step clockwise adds one sharp.', highlight: [0] },
  { title: 'The Sharp Keys (Clockwise)', text: 'C → G (1#) → D (2#) → A (3#) → E (4#) → B (5#) → F# (6#). Each step up a 5th adds one sharp. Remember: Going clockwise = going up in 5ths!', highlight: [0,1,2,3,4,5,6] },
  { title: 'The Flat Keys (Counter-Clockwise)', text: 'C → F (1b) → Bb (2b) → Eb (3b) → Ab (4b) → Db (5b). Each step counter-clockwise (up a 4th) adds one flat.', highlight: [0,11,10,9,8,7] },
  { title: 'Relative Minors', text: 'Every major key has a relative minor that shares the same key signature. The relative minor is always 3 half-steps below the major key. It\'s shown on the inner ring of the circle.', highlight: [0] },
  { title: 'The Pattern of 5ths', text: 'Here\'s the magic: if you know your 5ths, you can quickly figure out ANY key signature. A 5th up from any note = the next key clockwise. A 4th up = the next key counter-clockwise. Master this and you master the number system!', highlight: [0,1,2,3,4,5,6,7,8,9,10,11] }
];

function CircleDiagram(props) {
  var highlighted = props.highlighted || [];
  var activeKey = props.activeKey;
  var showMinors = props.showMinors;
  var onKeyClick = props.onKeyClick;
  var size = 320;
  var cx = size / 2;
  var cy = size / 2;
  var outerR = 130;
  var innerR = 90;

  return (
    <svg viewBox={'0 0 ' + size + ' ' + size} className="w-full max-w-[320px] mx-auto" data-testid="circle-diagram">
      <circle cx={cx} cy={cy} r={outerR + 15} fill="none" stroke="#E8E0D6" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={innerR - 10} fill="none" stroke="#E8E0D6" strokeWidth="1" strokeDasharray="4 4" />
      {KEYS_FIFTHS.map(function(key, i) {
        var angle = (i * 30 - 90) * (Math.PI / 180);
        var x = cx + outerR * Math.cos(angle);
        var y = cy + outerR * Math.sin(angle);
        var isHighlighted = highlighted.indexOf(i) !== -1;
        var isActive = activeKey === key;
        return (
          <g key={key} onClick={function() { if (onKeyClick) onKeyClick(key, i); }} style={{ cursor: onKeyClick ? 'pointer' : 'default' }}>
            <circle cx={x} cy={y} r={22} fill={isActive ? '#FF6B35' : isHighlighted ? '#FFF5F0' : 'white'} stroke={isActive ? '#FF6B35' : isHighlighted ? '#FF6B35' : '#E8E0D6'} strokeWidth={isActive || isHighlighted ? 2 : 1} />
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize="13" fontWeight={isActive || isHighlighted ? '700' : '500'} fill={isActive ? 'white' : isHighlighted ? '#FF6B35' : '#333'} fontFamily="monospace">
              {key}
            </text>
          </g>
        );
      })}
      {showMinors && MINOR_KEYS.map(function(key, i) {
        var angle = (i * 30 - 90) * (Math.PI / 180);
        var x = cx + innerR * Math.cos(angle);
        var y = cy + innerR * Math.sin(angle);
        var isActive = activeKey === KEYS_FIFTHS[i];
        return (
          <g key={key}>
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize="10" fontWeight="500" fill={isActive ? '#FF6B35' : '#999'} fontFamily="monospace">
              {key}
            </text>
          </g>
        );
      })}
      <text x={cx} y={cx - 8} textAnchor="middle" fontSize="9" fill="#aaa" fontWeight="600">5ths →</text>
      <text x={cx} y={cx + 8} textAnchor="middle" fontSize="9" fill="#aaa" fontWeight="600">← 4ths</text>
    </svg>
  );
}

function generateFifthsQuestion() {
  var types = ['fifth_above', 'fourth_above', 'relative_minor', 'key_signature'];
  var type = types[Math.floor(Math.random() * types.length)];
  var idx = Math.floor(Math.random() * 12);
  var key = KEYS_FIFTHS[idx];

  if (type === 'fifth_above') {
    var answer = KEYS_FIFTHS[(idx + 1) % 12];
    return { question: 'What key is a 5th above ' + key + '?', answer: answer, type: type, keyIdx: idx };
  }
  if (type === 'fourth_above') {
    var answer2 = KEYS_FIFTHS[(idx + 11) % 12];
    return { question: 'What key is a 4th above ' + key + '?', answer: answer2, type: type, keyIdx: idx };
  }
  if (type === 'relative_minor') {
    var answer3 = MINOR_KEYS[idx];
    return { question: 'What is the relative minor of ' + key + '?', answer: answer3, type: type, keyIdx: idx };
  }
  // key_signature
  var sf = SHARPS_FLATS[idx];
  var answer4 = sf.sig;
  return { question: 'How many sharps/flats in ' + key + ' major?', answer: answer4, type: type, keyIdx: idx };
}

function generateOptions(correct, type) {
  var pool = [];
  if (type === 'relative_minor') {
    pool = MINOR_KEYS.slice();
  } else if (type === 'key_signature') {
    pool = SHARPS_FLATS.map(function(s) { return s.sig; });
  } else {
    pool = KEYS_FIFTHS.slice();
  }
  var options = [correct];
  while (options.length < 4) {
    var pick = pool[Math.floor(Math.random() * pool.length)];
    if (options.indexOf(pick) === -1) options.push(pick);
  }
  for (var i = options.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var temp = options[i]; options[i] = options[j]; options[j] = temp;
  }
  return options;
}

function CircleOfFifths() {
  var [subTab, setSubTab] = useState('explore');
  var [activeKeyIdx, setActiveKeyIdx] = useState(0);
  var [showMinors, setShowMinors] = useState(true);

  // Learn mode
  var [learnStep, setLearnStep] = useState(0);

  // Practice mode
  var [question, setQuestion] = useState(null);
  var [options, setOptions] = useState([]);
  var [selected, setSelected] = useState(null);
  var [score, setScore] = useState({ correct: 0, total: 0, streak: 0, best: 0 });

  function newQuestion() {
    var q = generateFifthsQuestion();
    setQuestion(q);
    setOptions(generateOptions(q.answer, q.type));
    setSelected(null);
  }

  useEffect(function() {
    if (subTab === 'practice' && !question) newQuestion();
  }, [subTab]);

  function handleAnswer(opt) {
    if (selected !== null) return;
    setSelected(opt);
    var isCorrect = opt === question.answer;
    setScore(function(s) {
      var ns = isCorrect ? s.streak + 1 : 0;
      return { correct: s.correct + (isCorrect ? 1 : 0), total: s.total + 1, streak: ns, best: Math.max(s.best, ns) };
    });
    setTimeout(function() { newQuestion(); }, 1500);
  }

  var practiceAccuracy = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="circle-of-fifths">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-secondary w-full" data-testid="circle-subtabs">
          <TabsTrigger value="explore" className="flex-1 data-[state=active]:bg-accent data-[state=active]:text-white" data-testid="subtab-explore">
            Explore
          </TabsTrigger>
          <TabsTrigger value="learn" className="flex-1 data-[state=active]:bg-accent data-[state=active]:text-white" data-testid="subtab-learn">
            Learn
          </TabsTrigger>
          <TabsTrigger value="practice" className="flex-1 data-[state=active]:bg-accent data-[state=active]:text-white" data-testid="subtab-practice">
            Practice
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {subTab === 'explore' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <CircleDiagram highlighted={[activeKeyIdx]} activeKey={KEYS_FIFTHS[activeKeyIdx]} showMinors={showMinors} onKeyClick={function(key, i) { setActiveKeyIdx(i); }} />
          <div className="bg-card border-2 border-border rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold font-mono mb-1">{KEYS_FIFTHS[activeKeyIdx]} Major</div>
            <div className="text-sm text-muted-foreground mb-2">Relative minor: <span className="font-semibold text-foreground">{MINOR_KEYS[activeKeyIdx]}</span></div>
            <div className="text-sm text-muted-foreground">Key signature: <span className="font-semibold text-foreground">{SHARPS_FLATS[activeKeyIdx].sig}</span></div>
            <div className="flex justify-center gap-4 mt-3 text-xs text-muted-foreground">
              <span>5th up: <span className="font-mono font-bold text-accent">{KEYS_FIFTHS[(activeKeyIdx + 1) % 12]}</span></span>
              <span>4th up: <span className="font-mono font-bold text-accent">{KEYS_FIFTHS[(activeKeyIdx + 11) % 12]}</span></span>
            </div>
          </div>
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={function() { setActiveKeyIdx(function(i) { return (i + 11) % 12; }); }} data-testid="prev-key-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> 4th
            </Button>
            <Button variant="outline" size="sm" onClick={function() { setActiveKeyIdx(function(i) { return (i + 1) % 12; }); }} data-testid="next-key-btn">
              5th <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </motion.div>
      )}

      {subTab === 'learn' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <CircleDiagram highlighted={LEARN_STEPS[learnStep].highlight} activeKey={LEARN_STEPS[learnStep].highlight.length === 1 ? KEYS_FIFTHS[LEARN_STEPS[learnStep].highlight[0]] : null} showMinors={learnStep >= 4} />
          <div className="bg-card border-2 border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-accent" />
              <span className="text-xs text-muted-foreground font-mono">Step {learnStep + 1} of {LEARN_STEPS.length}</span>
            </div>
            <h3 className="font-bold text-lg mb-2">{LEARN_STEPS[learnStep].title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{LEARN_STEPS[learnStep].text}</p>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" disabled={learnStep === 0} onClick={function() { setLearnStep(function(s) { return s - 1; }); }} data-testid="learn-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex gap-1">
              {LEARN_STEPS.map(function(_, i) {
                return <div key={i} className={'w-2 h-2 rounded-full ' + (i === learnStep ? 'bg-accent' : i < learnStep ? 'bg-accent/30' : 'bg-border')} />;
              })}
            </div>
            <Button variant="outline" size="sm" disabled={learnStep === LEARN_STEPS.length - 1} onClick={function() { setLearnStep(function(s) { return s + 1; }); }} data-testid="learn-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </motion.div>
      )}

      {subTab === 'practice' && question && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <CircleDiagram highlighted={[question.keyIdx]} activeKey={KEYS_FIFTHS[question.keyIdx]} showMinors={question.type === 'relative_minor'} />
          <div className="grid grid-cols-4 gap-3 text-center text-sm">
            <div className="bg-card border border-border rounded-xl p-2">
              <div className="font-bold font-mono text-lg">{score.correct}</div>
              <div className="text-xs text-muted-foreground">Correct</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-2">
              <div className="font-bold font-mono text-lg">{practiceAccuracy}%</div>
              <div className="text-xs text-muted-foreground">Accuracy</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-2">
              <div className="font-bold font-mono text-lg text-accent">{score.streak}</div>
              <div className="text-xs text-muted-foreground">Streak</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-2">
              <div className="font-bold font-mono text-lg">{score.best}</div>
              <div className="text-xs text-muted-foreground">Best</div>
            </div>
          </div>
          <div className="bg-card border-2 border-border rounded-2xl p-5 text-center">
            <div className="text-xs uppercase tracking-wider text-accent font-semibold mb-2">{question.type.replace(/_/g, ' ')}</div>
            <div className="text-xl font-bold mb-4">{question.question}</div>
            <div className="grid grid-cols-2 gap-2">
              {options.map(function(opt, i) {
                var isCorrect = opt === question.answer;
                var isSelected = selected === opt;
                var cls = 'py-3 px-4 rounded-xl border-2 font-mono font-semibold text-base transition-all cursor-pointer ';
                if (selected !== null) {
                  if (isCorrect) cls += 'border-emerald-500 bg-emerald-50 text-emerald-700 ';
                  else if (isSelected) cls += 'border-rose-500 bg-rose-50 text-rose-700 ';
                  else cls += 'border-border bg-card text-muted-foreground ';
                } else {
                  cls += 'border-border bg-white hover:border-accent hover:bg-accent/5 ';
                }
                return (
                  <button key={i} className={cls} onClick={function() { handleAnswer(opt); }} disabled={selected !== null} data-testid={'practice-option-' + i}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" onClick={function() { setScore({ correct: 0, total: 0, streak: 0, best: 0 }); newQuestion(); }} data-testid="reset-practice">
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default CircleOfFifths;
